import React, { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Loader } from 'ncoded-component-library';
import AnalyticsCard from '../AnalyticsCard';
import { useTranslation } from 'react-i18next';
import { ResponsiveBar } from '@nivo/bar';
import api from 'api';
import EnterpriseContext from '../../../../providers/Enterprise/Enterprise.context';
import { EnterpriseAnalyticsDealRoomsByStatus } from 'models/EnterprisesAnalytics';
import { DEAL_STATUS_LABEL } from 'data/dealStatus';
import useMatchMedia from 'hooks/useMatchMedia';
import ReportsEmptyState from 'router/subrouters/DataRoom/subrouters/Reports/pages/Reports/components/ReportsEmptyState';
import useBarChart from 'hooks/useBarChart';

import './DealByStatusCard.styles.scss';
import './DealByStatusCard.styles.responsive.scss';

type DealByStatusCardProps = {
  className?: string;
};

const COLOR = '#0000fe';
const CHART_PADDING = 0.4;
const CHART_MARGIN = {
  top: 20,
  right: 0,
  bottom: 50,
  left: 30,
};
const AXIS = { tickSize: 0, tickPadding: 8 };

const DealByStatusCard: React.FC<DealByStatusCardProps> = (props) => {
  const { className } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [data, setData] = useState<EnterpriseAnalyticsDealRoomsByStatus[]>([]);

  const classes = classNames('deal-by-status-card', className);

  const { enterprise } = useContext(EnterpriseContext);

  const { t } = useTranslation();

  const isMobile = useMatchMedia('isMobile');

  const chartData = useMemo(
    () =>
      data.map(({ stage, count }) => ({
        stage: t(DEAL_STATUS_LABEL[stage]),
        value: count,
      })),
    [data, t],
  );

  const { gridYValues } = useBarChart(chartData.map(({ value }) => value));

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const { data } = await api.enterpriseAnalytics.getDealsByStatus(
          enterprise?._id,
        );

        setData(data);
        setIsEmpty(data.every(({ count }) => !count));
      } catch (e) {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [enterprise?._id]);

  return (
    <AnalyticsCard className={classes} title={t('currentDealsByStatus')}>
      {isLoading ? (
        <Loader />
      ) : isEmpty ? (
        <ReportsEmptyState />
      ) : (
        <div className="chart-wrapper">
          <ResponsiveBar
            indexBy="stage"
            data={chartData}
            enableLabel={false}
            isInteractive={false}
            padding={CHART_PADDING}
            margin={CHART_MARGIN}
            colors={COLOR}
            gridYValues={gridYValues}
            axisLeft={{
              ...AXIS,
              tickValues: gridYValues,
            }}
            axisBottom={
              isMobile
                ? {
                    ...AXIS,
                    tickRotation: -45,
                  }
                : AXIS
            }
          />
        </div>
      )}
    </AnalyticsCard>
  );
};

export default DealByStatusCard;
